import { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  ChevronRight,
  Save,
  X,
  Trash2,
  HelpCircle,
  AlertTriangle,
  ThumbsDown,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { useConfirm } from "@/components/ui/Confirm";
import { answerGap, dismissGap, type GapCluster, type GapReason } from "@/lib/api";
import { relative, absolute } from "@/lib/format";
import { cn } from "@/lib/cn";

interface GapCardProps {
  cluster: GapCluster;
  defaultOpen?: boolean;
  onResolved: (clusterId: string) => void;
}

/**
 * A single gap cluster. Shows:
 *   • The canonical question
 *   • Why the bot fell short (no retrieval, declined, negative feedback)
 *   • Occurrences + last asked
 *   • Variants (collapsed by default)
 *   • Two actions: "Answer this" (writes a new FAQ doc) and "Dismiss"
 *     (marks the cluster as not worth answering, so it stops appearing).
 *
 * Both actions notify the parent via onResolved so it can optimistically
 * remove the card from the open list.
 */
export function GapCard({ cluster, defaultOpen = false, onResolved }: GapCardProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(defaultOpen);
  const [showVariants, setShowVariants] = useState(false);
  const [title, setTitle]   = useState(cluster.canonical_question);
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const editorRef = useRef<HTMLDivElement | null>(null);

  // Scroll the editor into view when it opens via deep-link from Home.
  useEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [editing]);

  const extraVariants = cluster.variants.length - 1;

  const save = async () => {
    if (!answer.trim()) {
      toast.push("Write an answer before saving.", "error");
      return;
    }
    setSaving(true);
    try {
      await answerGap({
        title: title.trim() || cluster.canonical_question,
        answer: answer.trim(),
        canonical_question: cluster.canonical_question,
        variants: cluster.variants,
      });
      toast.push("Saved. Your bot will use this next time visitors ask.");
      onResolved(cluster.id);
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const dismiss = async () => {
    const ok = await confirm({
      title: "Dismiss this question?",
      description: "It will stop appearing in Content gaps. You can reopen it from the Resolved tab.",
      confirmLabel: "Dismiss",
      variant: "danger",
    });
    if (!ok) return;
    setDismissing(true);
    try {
      await dismissGap({
        canonical_question: cluster.canonical_question,
        variants: cluster.variants,
      });
      toast.push("Dismissed. The cluster moved to Resolved.");
      onResolved(cluster.id);
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Failed to dismiss", "error");
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div className="card">
      {/* Header row — question + reason + counts + CTAs */}
      <div className="flex items-start justify-between gap-4 px-5 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <ReasonBadge reason={cluster.primary_reason} />
            <p className="min-w-0 flex-1 text-sm font-medium text-text-strong">
              &ldquo;{cluster.canonical_question}&rdquo;
            </p>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 pl-[26px] text-xs text-text-soft">
            <span className="inline-flex items-center gap-1">
              <Users size={12} strokeWidth={1.75} className="text-text-faint" />
              <span className="tabular-nums">{cluster.total_occurrences}</span>{" "}
              visitor{cluster.total_occurrences === 1 ? "" : "s"} asked
            </span>
            {cluster.last_asked_at && (
              <>
                <span className="text-text-faint">·</span>
                <span title={absolute(cluster.last_asked_at)} className="cursor-default">
                  last asked {relative(cluster.last_asked_at)}
                </span>
              </>
            )}
            {extraVariants > 0 && (
              <>
                <span className="text-text-faint">·</span>
                <button
                  type="button"
                  onClick={() => setShowVariants((v) => !v)}
                  className="inline-flex items-center gap-0.5 text-coral hover:text-coral-hover"
                  aria-expanded={showVariants}
                >
                  {showVariants ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {extraVariants} similar {extraVariants === 1 ? "variant" : "variants"}
                </button>
              </>
            )}
          </div>
        </div>

        {!editing && (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              loading={dismissing}
              iconLeft={!dismissing && <Trash2 size={12} strokeWidth={1.75} />}
              title="Dismiss this cluster — won't appear again unless you reopen it."
            >
              Dismiss
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setEditing(true)}
            >
              Answer this
            </Button>
          </div>
        )}
      </div>

      {/* Variants list (collapsed by default) */}
      {showVariants && extraVariants > 0 && (
        <div className="border-t border-line bg-surface-2">
          <ul className="divide-y divide-line">
            {cluster.variants.slice(1).map((v, i) => (
              <li key={i} className="flex items-center justify-between gap-4 px-5 py-2.5 text-xs">
                <span className="truncate text-text">&ldquo;{v.question}&rdquo;</span>
                <span className="shrink-0 tabular-nums text-text-soft">
                  {v.occurrences}× · {relative(v.last_asked_at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Inline editor */}
      {editing && (
        <div ref={editorRef} className="border-t border-line bg-surface-2 px-5 py-4">
          <label className="block">
            <span className="text-xs font-medium text-text-strong">Question title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Pet policy for large dogs"
              className="mt-1.5 block w-full rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none focus:border-coral"
            />
            <span className="mt-1 block text-2xs text-text-soft">
              Used as the document title in your knowledge base.
            </span>
          </label>

          <label className="mt-3 block">
            <span className="text-xs font-medium text-text-strong">Answer (markdown)</span>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={6}
              placeholder="Write the answer your bot should give next time someone asks this. Markdown is supported."
              className={cn(
                "mt-1.5 block w-full resize-y rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none focus:border-coral",
                "font-mono",
              )}
            />
            <span className="mt-1 block text-2xs text-text-soft">
              Your answer becomes an FAQ document, gets embedded, and starts grounding bot replies immediately.
            </span>
          </label>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditing(false)}
              disabled={saving}
              iconLeft={<X size={12} strokeWidth={1.75} />}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              loading={saving}
              iconLeft={!saving && <Save size={12} strokeWidth={1.75} />}
            >
              {saving ? "Saving…" : "Save answer"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReasonBadge — tiny icon + label per gap explaining WHY it's a gap.
// ---------------------------------------------------------------------------

const REASON_META: Record<GapReason, { icon: typeof HelpCircle; label: string; tone: string; help: string }> = {
  declined: {
    icon: AlertTriangle,
    label: "Declined",
    tone: "text-warning",
    help: "The bot had some context but still told the visitor it didn't have the answer. Usually means your knowledge base is missing this specific topic.",
  },
  no_retrieval: {
    icon: HelpCircle,
    label: "No match",
    tone: "text-text-soft",
    help: "The bot's vector search found nothing similar in your knowledge base.",
  },
  negative_feedback: {
    icon: ThumbsDown,
    label: "Downvoted",
    tone: "text-danger",
    help: "A visitor explicitly thumbed-down the bot's response.",
  },
};

function ReasonBadge({ reason }: { reason: GapReason | null }) {
  if (!reason) return null;
  const meta = REASON_META[reason];
  const Icon = meta.icon;
  return (
    <span
      title={meta.help}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded bg-surface-2 px-1.5 py-0.5 text-2xs",
        meta.tone,
      )}
    >
      <Icon size={11} strokeWidth={1.75} />
      {meta.label}
    </span>
  );
}
